import { useResource } from 'app/state/resource'
import { statsResource } from 'features/account/state/accountSlice'
import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { useMemo } from 'react'
import { QuotaState, QuotaTypes } from 'features/quotas/types'
import { calculateQuotas, getQuotaDefinitions, useQuotaContext } from 'features/quotas/utils'
import { quotaDefinitions } from 'features/quotas/quotas'
import { calculateDaysDifference } from 'features/common/utils/calculateDaysDifference'
import { AccountStatsDetails } from 'features/account/types'
import { addDays, subDays, compareAsc } from 'date-fns'

// Check if user exceeded soft quota
export function useAccountSoftQuotas() {
  const quotaContext = useQuotaContext()
  return useMemo(() => {
    const allQuotas = getQuotaDefinitions(quotaDefinitions, quotaContext)
    const softQuotas = (Object.keys(allQuotas) as QuotaTypes[]).reduce((r, key) => {
      const quota = allQuotas[key] as QuotaState
      if (quota.soft) {
        r[key] = quota
      }
      return r
    }, {} as Record<QuotaTypes, QuotaState>)

    return calculateQuotas(softQuotas)
  }, [quotaContext])
}

export function useAccountQuotas() {
  const { usageBasedPricing } = useGetAccountData()
  const quotaContext = useQuotaContext()

  return useMemo(() => {
    const usageBasedPricingStats = { ...quotaContext.stats }

    if (usageBasedPricingStats) {
      Object.keys(usageBasedPricingStats).forEach((key) => {
        if (!['lds', 'users', 'editorUsers', 'viewerUsers', 'tokenCount'].includes(key)) {
          usageBasedPricingStats[key] = 0
        }
      })
    }

    const context = usageBasedPricing
      ? {
          ...quotaContext,
          stats: usageBasedPricingStats as AccountStatsDetails
        }
      : quotaContext

    return calculateQuotas(getQuotaDefinitions(quotaDefinitions, context))
  }, [usageBasedPricing, quotaContext])
}

export function useAccountUsageQuotas() {
  const accountData = useGetAccountData()
  const { data: stats } = useResource(statsResource)
  const now = new Date()
  const totalQuota = accountData?.quotas?.usageQuota
  const usedQuota = stats?.usageQuota ? Math.ceil(stats?.usageQuota) : 0
  const resetDay = accountData?.quotas?.usageQuotaResetDay
  const remainingQuota = totalQuota - usedQuota
  const isExceeded = usedQuota >= totalQuota
  const creationDate = new Date(Date.parse(accountData.createdWhen))
  const resetDate = new Date(Date.parse(resetDay))
  const startDate = subDays(resetDate, 365)
  const startPeriodDate = compareAsc(creationDate, startDate) === 1 ? creationDate : startDate
  const usedDays = calculateDaysDifference(startPeriodDate, now) || 0
  const maxUsagePerMonth = Math.ceil(totalQuota / 12)
  const currentUsagePerDay = usedDays === 0 ? usedQuota : Math.ceil(usedQuota / usedDays)
  const currentUsagePerMonth = usedDays === 0 ? usedQuota : currentUsagePerDay * 30
  const usageQuotaPerMonthIsExceeding = currentUsagePerMonth >= maxUsagePerMonth
  const usageRemainingDays = Math.ceil(remainingQuota / currentUsagePerDay)
  const usageQuotaEndDate = addDays(now, usageRemainingDays)

  return {
    totalQuota,
    usedQuota,
    usageQuotaResetDay: resetDay,
    usageQuotaIsExceeded: isExceeded,
    usageQuotaPerMonthIsExceeding,
    usageQuotaEndDate,
    maxUsagePerMonth,
    currentUsagePerMonth
  }
}
